import React, { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { Alert, Button, Popover, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { COLORS } from "../../../consts";
import { useUser } from "../../../context/UserContext";
import { hexToRgba } from "./utils";

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildGrowthRateSeries = (labels, values, format) => {
  let previousValue = null;
  let previousDate = null;

  return labels.map((label, index) => {
    const currentValue = toFiniteNumber(values[index]);
    const currentDate = dayjs(label, format);
    if (!currentDate.isValid() || currentValue === null) {
      return null;
    }

    if (previousValue === null || !previousDate?.isValid()) {
      previousValue = currentValue;
      previousDate = currentDate;
      return null;
    }

    const dayDifference = Math.max(currentDate.diff(previousDate, "day"), 1);
    const growthRate = (currentValue - previousValue) / dayDifference;
    previousValue = currentValue;
    previousDate = currentDate;
    return Number.isFinite(growthRate) ? growthRate : null;
  });
};

const AverageGraph = ({
  averageData,
  visibility,
  setVisibility,
  isImperial = false,
  isFullscreen = false,
}) => {
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
  const { userData } = useUser();
  const isSuperUser = Boolean(userData?.is_superuser);
  const averageLineLabel = isImperial ? "Average Broccoli Head Size (inches)" : "Average Broccoli Head Size (cm)";

  const [graphType, setGraphType] = useState("line");
  const [advancedVisibility, setAdvancedVisibility] = useState({
    avgGrowthRate: true,
    growthBaseline: true,
    stdDev: true,
    coefficientVariation: true,
  });

  const sortedAverageData = useMemo(
    () =>
      [...(averageData || [])].sort((a, b) =>
        dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
          dayjs(b.date, GRAPH_UI_DATE_FORMAT),
        ),
      ),
    [averageData],
  );

  const labels = useMemo(
    () => sortedAverageData.map((point) => point.date),
    [sortedAverageData],
  );

  const averageSeries = useMemo(
    () => sortedAverageData.map((point) => toFiniteNumber(point.averageLength)),
    [sortedAverageData],
  );

  const stdDevSeries = useMemo(
    () => sortedAverageData.map((point) => toFiniteNumber(point.stdDev)),
    [sortedAverageData],
  );

  const coefficientVariationSeries = useMemo(
    () =>
      sortedAverageData.map((point) => {
        const avg = toFiniteNumber(point.averageLength);
        const std = toFiniteNumber(point.stdDev);
        if (avg === null || avg === 0 || std === null) return null;
        return (std / avg) * 100;
      }),
    [sortedAverageData],
  );

  const growthRateSeries = useMemo(
    () => buildGrowthRateSeries(labels, averageSeries, GRAPH_UI_DATE_FORMAT),
    [labels, averageSeries],
  );

  const averageChartData = {
    labels,
    datasets: [
      {
        label: averageLineLabel,
        data: averageSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.3,
        hidden: !visibility[averageLineLabel],
        spanGaps: true,
      },
      {
        label: "Upper Standard Deviation",
        data: sortedAverageData.map((point) => {
          const avg = toFiniteNumber(point.averageLength);
          const std = toFiniteNumber(point.stdDev);
          if (avg === null || std === null) return null;
          return avg + std;
        }),
        borderColor: "#a9cff5",
        backgroundColor: hexToRgba("#a9cff5", 0.1),
        fill: "-1",
        tension: 0.3,
        hidden: !visibility["Standard Deviation"],
        spanGaps: true,
      },
      {
        label: "Lower Standard Deviation",
        data: sortedAverageData.map((point) => {
          const avg = toFiniteNumber(point.averageLength);
          const std = toFiniteNumber(point.stdDev);
          if (avg === null || std === null) return null;
          return avg - std;
        }),
        borderColor: "#a9cff5",
        backgroundColor: hexToRgba("#a9cff5", 0.1),
        fill: "-1",
        tension: 0.3,
        hidden: !visibility["Standard Deviation"],
        spanGaps: true,
      },
    ],
  };

  const growthRateChartData = {
    labels,
    datasets: [
      {
        label: "Average Growth Rate",
        data: growthRateSeries,
        borderColor: COLORS.primaryBlue,
        backgroundColor: COLORS.primaryBlue,
        fill: false,
        tension: 0.3,
        spanGaps: true,
        hidden: !advancedVisibility.avgGrowthRate,
      },
      {
        label: "No-Change Baseline",
        data: labels.map(() => 0),
        borderColor: "#8c8c8c",
        backgroundColor: "#8c8c8c",
        fill: false,
        tension: 0,
        borderDash: [6, 4],
        pointRadius: 0,
        spanGaps: true,
        hidden: !advancedVisibility.growthBaseline,
      },
    ],
  };

  const stabilityChartData = {
    labels,
    datasets: [
      {
        label: "Head Size Variability (Std Dev)",
        data: stdDevSeries,
        borderColor: COLORS.primaryOrange,
        backgroundColor: COLORS.primaryOrange,
        fill: false,
        tension: 0.3,
        spanGaps: true,
        hidden: !advancedVisibility.stdDev,
        yAxisID: "y",
      },
      {
        label: "Consistency Score (CV %)",
        data: coefficientVariationSeries,
        borderColor: "#722ed1",
        backgroundColor: "#722ed1",
        fill: false,
        tension: 0.3,
        spanGaps: true,
        hidden: !advancedVisibility.coefficientVariation,
        yAxisID: "y1",
      },
    ],
  };

  const averageOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            const unit = isImperial ? "in" : "cm";
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: isImperial ? "Broccoli Head Size (inches)" : "Broccoli Head Size (cm)", font: { size: 16 } },
        grid: { display: false },
        beginAtZero: false,
        ticks: { font: { size: 14 } },
      },
    },
  };

  const growthRateOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            const unit = isImperial ? "in/day" : "cm/day";
            return value === null
              ? `${tooltipItem.dataset.label}: N/A`
              : `${tooltipItem.dataset.label}: ${Number(value).toFixed(3)} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: isImperial ? "Growth Rate (in/day)" : "Growth Rate (cm/day)", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const stabilityOptions = {
    responsive: true,
    maintainAspectRatio: !isFullscreen,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            if (value === null) return `${tooltipItem.dataset.label}: N/A`;
            if (tooltipItem.dataset.yAxisID === "y1") {
              return `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)}%`;
            }
            const unit = isImperial ? "in" : "cm";
            return `${tooltipItem.dataset.label}: ${Number(value).toFixed(2)} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Dates", font: { size: 16 } },
        grid: { display: false },
        ticks: { autoSkip: false, font: { size: 14 } },
      },
      y: {
        title: { display: true, text: isImperial ? "Standard Deviation (inches)" : "Standard Deviation (cm)", font: { size: 16 } },
        grid: { display: false },
        ticks: { font: { size: 14 } },
      },
      y1: {
        position: "right",
        title: { display: true, text: "Consistency (CV %)", font: { size: 16 } },
        grid: { display: false, drawOnChartArea: false },
        ticks: { font: { size: 14 } },
      },
    },
  };

  const graphTypeOptions = [
    { label: "Line", value: "line" },
    { label: "Growth Rate", value: "growthRate" },
    { label: "Stability", value: "stability" },
  ];

  const graphInfoByType = {
    line: {
      short:
        "Line Trend: Tracks absolute broccoli head-size movement across dates.",
      relevance:
        "Best for direct review of head-size trajectory and spread range over time.",
      details: [
        "Average line uses averageLength per date.",
        "Head-size range band uses averageLength +/- stdDev.",
      ],
    },
    growthRate: {
      short:
        "Growth Rate: Shows day-over-day head-size change speed (cm/day).",
      relevance:
        "Best for spotting acceleration or slowdown in growth velocity.",
      details: [
        "Rate is calculated as: (current size - previous size) / day difference.",
        "Positive values indicate growth; near-zero values indicate flattening.",
      ],
    },
    stability: {
      short:
        "Stability: Tracks variability and consistency of head sizes across dates.",
      relevance:
        "Best for evaluating uniformity approaching harvest windows.",
      details: [
        "Std Dev is taken from graph data.",
        "CV% is calculated as: (stdDev / average size) * 100.",
      ],
    },
  };

  const activeGraphInfo = graphInfoByType[graphType] || graphInfoByType.line;

  const advancedLegendByGraphType = {
    growthRate: [
      {
        key: "avgGrowthRate",
        label: "Average Growth Rate",
        color: COLORS.primaryBlue,
      },
      {
        key: "growthBaseline",
        label: "No-Change Baseline",
        color: "#8c8c8c",
      },
    ],
    stability: [
      {
        key: "stdDev",
        label: "Head Size Variability (Std Dev)",
        color: COLORS.primaryOrange,
      },
      {
        key: "coefficientVariation",
        label: "Consistency Score (CV %)",
        color: "#722ed1",
      },
    ],
  };

  const activeChartData =
    graphType === "growthRate"
      ? growthRateChartData
      : graphType === "stability"
      ? stabilityChartData
      : averageChartData;

  const activeChartOptions =
    graphType === "growthRate"
      ? growthRateOptions
      : graphType === "stability"
      ? stabilityOptions
      : averageOptions;

  const isDefaultLineType = graphType === "line";
  const currentAdvancedLegend = advancedLegendByGraphType[graphType] || [];

  const toggleLineVisibility = (label) => {
    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [label]: !prevVisibility[label],
    }));
  };

  const toggleAdvancedVisibility = (key) => {
    setAdvancedVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className={`flex w-full flex-col ${isFullscreen ? "h-full min-h-0" : ""}`}>
      <h2 className={`font-bold ${isFullscreen ? "text-xl" : "text-2xl"}`}>
        Brocolli Head Growth Trend
      </h2>
      <div
        className={`graph-header w-full flex justify-between items-center sm:flex-col ${
          isFullscreen ? "mb-2 gap-2" : "mb-4"
        }`}
      >
        {isDefaultLineType ? (
          <div className="custom-legend flex gap-3 sm:flex-col">
            <div
              key="avg"
              className="flex items-center mb-1 cursor-pointer"
              onClick={() =>
                toggleLineVisibility(averageLineLabel)
              }
            >
              <div
                className="relative w-10 h-0.5 mr-2"
                style={{
                  backgroundColor: COLORS.primaryBlue,
                  opacity: visibility[averageLineLabel] ? 1 : 0.3,
                }}
              >
                <div
                  style={{
                    backgroundColor: COLORS.primaryBlue,
                    opacity: visibility[averageLineLabel] ? 1 : 0.3,
                  }}
                  className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                ></div>
              </div>
              <span
                style={{
                  opacity: visibility[averageLineLabel] ? 1 : 0.3,
                }}
              >
                Average
              </span>
            </div>
            <div
              key="standard_deviation"
              className="flex items-center mb-1 cursor-pointer"
              onClick={() => toggleLineVisibility("Standard Deviation")}
            >
              <div
                className="relative w-10 h-0.5 mr-2"
                style={{
                  backgroundColor: "#a9cff5",
                  opacity: visibility["Standard Deviation"] ? 1 : 0.3,
                }}
              >
                <div
                  style={{
                    backgroundColor: "#a9cff5",
                    opacity: visibility["Standard Deviation"] ? 1 : 0.3,
                  }}
                  className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                ></div>
              </div>
              <span
                style={{
                  opacity: visibility["Standard Deviation"] ? 1 : 0.3,
                }}
              >
                Standard Deviation
              </span>
            </div>
          </div>
        ) : (
          <div className="custom-legend flex flex-wrap gap-3 sm:flex-col">
            {currentAdvancedLegend.map((item) => (
              <div
                key={item.key}
                className="flex items-center mb-1 cursor-pointer"
                onClick={() => toggleAdvancedVisibility(item.key)}
              >
                <div
                  className="relative w-10 h-0.5 mr-2"
                  style={{
                    backgroundColor: item.color,
                    opacity: advancedVisibility[item.key] ? 1 : 0.3,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: item.color,
                      opacity: advancedVisibility[item.key] ? 1 : 0.3,
                    }}
                    className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full"
                  ></div>
                </div>
                <span
                  style={{
                    opacity: advancedVisibility[item.key] ? 1 : 0.3,
                  }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {isSuperUser && (
          <div className={isFullscreen ? "w-full flex justify-end sm:justify-start" : "ml-auto"}>
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-semibold"
                style={{ color: COLORS.darkerGray }}
              >
                Graph Type:
              </span>
              <Select
                value={graphType}
                onChange={setGraphType}
                options={graphTypeOptions}
                className="w-[210px]"
              />
            </div>
          </div>
        )}
      </div>
      {isSuperUser && (
        <Alert
          type="info"
          showIcon={false}
          className={`${isFullscreen ? "mb-2" : "mb-3"} !py-1`}
          style={{ border: "none", backgroundColor: "transparent" }}
          message={
            <div className="text-xs leading-5" style={{ color: COLORS.primaryBlue }}>
              <span className="font-semibold">{activeGraphInfo.short}</span>{" "}
              <span>{activeGraphInfo.relevance}</span>
              <Popover
                trigger="click"
                placement="bottomRight"
                title="How this graph is calculated"
                content={
                  <div className="max-w-[380px] text-xs leading-5">
                    <div className="mb-1 font-semibold">
                      {activeGraphInfo.relevance}
                    </div>
                    {activeGraphInfo.details.map((detail) => (
                      <div key={detail}>{detail}</div>
                    ))}
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Graph calculation details"
                  icon={<InfoCircleOutlined style={{ color: COLORS.primaryBlue }} />}
                  className="!ml-1 !h-auto !w-auto !min-w-0 !p-0 align-middle"
                />
              </Popover>
            </div>
          }
        />
      )}
      <div
        className={`w-full ${
          isFullscreen
            ? "min-h-0 flex-1 overflow-x-auto overflow-y-hidden"
            : "overflow-x-auto"
        }`}
      >
        <div className={isFullscreen ? "h-full min-w-[760px]" : "min-w-[640px]"}>
          <Line data={activeChartData} options={activeChartOptions} />
        </div>
      </div>
    </div>
  );
};

export { AverageGraph };
